import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ErrorMessage } from '@hookform/error-message';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { isNumber } from 'lodash';
import cx from 'classnames';

import { setRefreshTokenLocalStorage, setTokenLocalStorage } from '../../../../helpers/localStorage';
import { forgotPassword, getMyUserInfo, loginWithEmailAndPassword } from '../../../../api/usersApi';
import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import { removeCurrentUser, saveUser } from '../../../../store/user/userSlice';
import { ORGANIZATION, PATHS, RolesLowerCase } from '../../../../enums';
import SearchInput from '../../../../components/modules/SearchInput';
import RolePlate from '../../../../components/modules/RolePlate';
import {
  bootstrapComposerWebPreferences,
  displayRole,
  isSafari,
} from '../../../../helpers';
import { ReactComponent as AmplifyLogo } from '../../../../assets/amplify-colors.svg';
import CrossIcon from '../../../../assets/icons/cross.svg';
import Avatar from '../../../../components/modules/Avatar';
import Modal from '../../../../components/modules/Modal';
import { useAppDispatch } from '../../../../store/hooks';
import { NOTIFICATION } from '../../../../enums/ENUMS';
import styles from './LoginModal.module.scss';

type TLoginModal = {
  open: boolean;
  switchModal: () => void;
  disableClose?: boolean;
};

const isSafariBrowser = isSafari();

const LoginModal: React.FC<TLoginModal> = ({ open, switchModal, disableClose = false }) => {
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true,
    },
  });
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [currentModal, setCurrentModal] = useState('login');
  const onClose = () => {
    if (disableClose) return;

    setCurrentModal('login');
    setStep(1);
    setSelectedOrg(null);
    setUserOrgs([]);
    switchModal();
    setIsSuperAdmin(false);
    setSelectedSA(false);
  };
  const [passwordShown, setPasswordShown] = useState(false);
  const togglePasswordVisiblity = () => {
    setPasswordShown(!passwordShown);
  };

  const switchToForgotPass = () => {
    setCurrentModal('forgotPassword');
  };

  const switchToLogin = () => {
    setCurrentModal('login');
  };

  const [step, setStep] = useState(1);
  const [userOrgs, setUserOrgs] = useState<ORGANIZATION[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<ORGANIZATION | null>(null);
  const [selectedSA, setSelectedSA] = useState(false);
  const [searchOrg, setSearchOrg] = useState('');
  const [autObj, setAuthObj] = useState({
    email: '',
    password: '',
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const selectSuperAdmin = () => {
    setSelectedOrg(null);
    setSelectedSA(true);
  };
  useEffect(() => {
    if (selectedOrg && isSuperAdmin) {
      setSelectedSA(false);
    }
  }, [selectedOrg, isSuperAdmin]);

  const onSubmit = async (data: { email: string; password: string; rememberMe: boolean }) => {
    setLoading(true);
    try {
      const res = await loginWithEmailAndPassword({
        email: data.email.replace(/\s/g, ''),
        password: data.password.replace(/\s/g, ''),
        selectedOrgId: isNumber(selectedOrg?.id) ? `${selectedOrg?.id}` : selectedOrg?.id,
        asSuperAdmin: selectedSA,
      });
      if (!res?.data?.accessToken && !res?.data?.refreshToken && res?.data?.organizations.length && res?.data?.message) {
        notification.showMessage(res.data.message);
        const uniqueOrganizations = [...res.data.organizations];
        if (res.data?.isSuperAdmin) {
          setIsSuperAdmin(true);
        }
        setUserOrgs(uniqueOrganizations);
        setStep(2);
        setAuthObj({
          email: data.email,
          password: data.password,
        });
      } else {
        setTokenLocalStorage(res.data.accessToken);
        setRefreshTokenLocalStorage(res.data.refreshToken);
        const user = await getMyUserInfo();
        await bootstrapComposerWebPreferences();
        dispatch(saveUser(user.data));
        notification.showMessage(NOTIFICATION.LOGGED_IN);
        switchModal();
        if (user?.data?.role?.title === RolesLowerCase.SUPER_ADMIN) {
          history.push(`${PATHS.ADMIN_PAGE.path}`);
        } else if (user?.data?.role?.title === RolesLowerCase.VIEWER) {
          history.push(`${PATHS.SHARED_WITH_ME.path}`);
        } else {
          history.push(`${PATHS.MY_FOLDERS.path}`);
        }
      }
    } catch (err: any) {
      console.log(err);
      if (err?.response?.data?.message) {
        if (typeof err.response.data.message === 'string') {
          notification.showMessage(err.response.data.message);
        } else if (err.response.data.message?.message?.[0]) {
          notification.showMessage(err.response.data.message.message[0]);
        }
      } else {
        notification.showMessage('Network Error');
      }
      dispatch(removeCurrentUser());
    } finally {
      setLoading(false);
    }
  };
  const handleForgotPassword = async (data: { email: string }) => {
    setLoading(true);
    try {
      await forgotPassword({ email: data.email });
      notification.showMessage('An email was sent to your account');
      onClose();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    setStep(1);
    setUserOrgs([]);
    setSelectedOrg(null);
    setIsSuperAdmin(false);
    setSelectedSA(false);
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading} className={styles.loginModal}>
      {currentModal === 'login' ? (
        <div className={cx(styles.modalBody, step === 2 && styles.organisationStep)}>
          {!disableClose && <img src={CrossIcon} alt="close modal" onClick={onClose} className={styles.closeIcon} />}
          <div className={styles.logoWrap}>
            <AmplifyLogo aria-label="Amplify" className={styles.logo} />
          </div>
          <div className={styles.productTitle}>AMPLIFY</div>
          <div className={styles.companyTitle}>Fulcrum Technologies</div>

          {step === 1 && (
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              <label className={styles.fieldLabel} htmlFor="login-email">
                USERNAME
              </label>
              <input
                id="login-email"
                {...register('email')}
                className={styles.field}
                placeholder="Enter your username"
                name="email"
                type="email"
                style={{ border: errors?.email && '1px solid #FF2323' }}
              />
              <ErrorMessage errors={errors} name="email" render={({ message }) => <div className="errorMessage">{message}</div>} />

              <label className={styles.fieldLabel} htmlFor="login-password">
                PASSWORD
              </label>
              <div className={styles.relativeContainer}>
                <input
                  id="login-password"
                  {...register('password', {
                    required: 'Enter password',
                    minLength: {
                      value: 8,
                      message: 'Password must contain at least 8 characters',
                    },
                  })}
                  className={styles.field}
                  style={{ border: errors?.password && '1px solid #FF2323' }}
                  placeholder="Enter your password"
                  name="password"
                  type={passwordShown ? 'text' : 'password'}
                />

                {isSafariBrowser ? (
                  <VisibilityIcon className={styles.eye} onClick={togglePasswordVisiblity} />
                ) : (
                  <div className={styles.eye} onClick={togglePasswordVisiblity}>
                    {!passwordShown ? 'Show' : 'Hide'}
                  </div>
                )}
              </div>
              <ErrorMessage errors={errors} name="password" render={({ message }) => <div className="errorMessage">{message}</div>} />

              <div className={styles.actionBtns}>
                <ContainedButton disabled={loading} className={styles.loginButton} type="submit">
                  Sign In
                </ContainedButton>
                <div className={styles.addMore}>
                  <span onClick={switchToForgotPass}>Forgot Password?</span>
                </div>
              </div>
            </form>
          )}

          {step === 2 && (
            <>
              <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>Select Organisation</div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div onClick={goToLogin} className={styles.step}>
                    Step {step}/2
                  </div>
                </div>
              </div>

              <div className={styles.form}>
                <SearchInput onChange={(e) => setSearchOrg(e.target.value)} placeHolder="Search by name" className={styles.search} />

                <div className={styles.orgList}>
                  {userOrgs
                    .filter(
                      (org) =>
                        org?.name?.toLocaleLowerCase()?.includes(searchOrg?.toLocaleLowerCase()) ||
                        org?.location?.toLocaleLowerCase()?.includes(searchOrg?.toLocaleLowerCase()),
                    )
                    .map((org) => (
                      <div
                        key={org.id}
                        onClick={() => setSelectedOrg(org)}
                        className={cx(styles.org, selectedOrg?.id === org.id ? styles.orgSelected : '')}
                      >
                        <Avatar avatar={org?.image_url} name={org.name} />
                        <div className={styles.orgInfo}>
                          <div className={styles.orgTitle}>{org.name}</div>
                          <div className={styles.orgLocation}>{org.location}</div>{' '}
                        </div>
                        {org?.role?.title && <RolePlate className={styles.rolePlate} userRole={displayRole(org?.role?.title)} />}
                      </div>
                    ))}

                  {isSuperAdmin && (
                    <div
                      style={{ padding: '8px', height: '38px' }}
                      onClick={selectSuperAdmin}
                      className={cx(styles.org, selectedSA ? styles.orgSelected : '')}
                    >
                      <RolePlate userRole={'Super Admin'} style={{ fontSize: '15px' }} />
                      <div className={styles.orgInfo}>
                        <div className={styles.orgTitle} style={{ color: 'white' }}>
                          Login as Super Admin
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.actionBtns}>
                  <ContainedButton
                    className={styles.loginButton}
                    disabled={loading || (!selectedOrg && !selectedSA)}
                    onClick={() => onSubmit({ email: autObj.email, password: autObj.password, rememberMe: true })}
                  >
                    Sign In
                  </ContainedButton>
                  {!disableClose && (
                    <OutlinedButton kind="default" disabled={loading} onClick={onClose}>
                      Cancel
                    </OutlinedButton>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={styles.modalBody}>
          {!disableClose && <img src={CrossIcon} alt="close modal" onClick={onClose} className={styles.closeIcon} />}
          <div className={styles.logoWrap}>
            <AmplifyLogo aria-label="Amplify" className={styles.logo} />
          </div>
          <div className={styles.productTitle}>AMPLIFY</div>
          <div className={styles.companyTitle}>Fulcrum Technologies</div>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>Enter your email address</div>
          </div>
          <p className={styles.helperText}>We will send you a link to reset password</p>
          <form
            className={styles.form}
            onSubmit={handleSubmit((data) => {
              handleForgotPassword(data);
            })}
          >
            <input {...register('email')} className={styles.field} placeholder="Email Address" name="email" type="email" />
            <ErrorMessage errors={errors} name="email" render={({ message }) => <div className="errorMessage">{message}</div>} />
            <div className={styles.actionBtns}>
              <ContainedButton className={styles.forgotButton} disabled={loading} type="submit">
                Send
              </ContainedButton>
              <div className={styles.addMore}>
                <span onClick={switchToLogin}>Back to Sign In</span>
              </div>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
};

export default LoginModal;
